import { Radio, Stack, Tooltip, Typography } from '@mui/material';
import { useEffect, useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import Icon from '/@/components/icon';
import colors from '/@/components/theme-provider/colors';
import settings from '/@/core/settings';
import { getCropProtectionTypeOptions } from '/@/store/crop-protection/selectors';
import type { CropProtectionForm } from '/@/types/crop-protection';

const CropProtectionTypeStep = ({
    defaultType,
    form,
}: {
    defaultType?: string;
    form: UseFormReturn<CropProtectionForm>;
}) => {
    useEffect(() => {
        form.reset({
            ...form.getValues(),
            application: '',
            weedType: '',
            weed: '',
            weedSize: '',
            recommendationOption: '',
            diseaseId: '',
            applicationType: '',
            plagueId: '',
        });
    }, [form]);
    const crop = form.watch('crop');
    const options = useMemo(() => getCropProtectionTypeOptions(crop), [crop]);
    useEffect(() => {
        if (!defaultType) return;
        form.setValue('type', defaultType);
    }, [form, defaultType]);

    const handleChange = (key: 'weeds' | 'plagues' | 'diseases' | 'seed_treatment' | '') => {
        form.setValue('type', key);
    };

    return (
        <Stack
            sx={{
                display: 'grid',
                gap: 3,
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gridAutoRows: '1fr',
            }}
        >
            {Object.entries(options).map(([key, value]) => (
                <CardList
                    key={key}
                    title={value.title}
                    subTitle={value.subtitle}
                    description={value.description()}
                    rightItem={
                        <Icon
                            padding={false}
                            size='xs'
                            variant={'accent'}
                            theme={value.disabled() ? null : settings.app.theme}
                            type={value.icon}
                        />
                    }
                    isSelected={form.watch('type') === key}
                    onClick={() => handleChange(key)}
                    disabled={value.disabled()}
                    disabledTooltip={value.disabledTooltip}
                />
            ))}
        </Stack>
    );
};

export default CropProtectionTypeStep;

function CardList({
    title,
    subTitle,
    description,
    rightItem,
    onClick,
    isSelected,
    disabled,
    disabledTooltip,
}: CardListProps) {
    return (
        <Tooltip title={disabled && disabledTooltip} arrow>
            <Stack
                border={
                    isSelected
                        ? `2px solid ${colors.themes[settings.app.theme].mui.secondary.secondary}`
                        : '1px solid lightgray'
                }
                alignItems='center'
                backgroundColor={
                    isSelected ? colors.themes[settings.app.theme].mui.primary.itemSelectedBackground : undefined
                }
                onClick={disabled ? undefined : onClick}
                borderRadius='4px'
                sx={disabled ? {} : { cursor: 'pointer' }}
            >
                <Stack
                    direction='row'
                    justifyContent='space-between'
                    alignItems='center'
                    px={2}
                    py={1.5}
                    width='100%'
                    borderBottom={description && '1px solid lightgray'}
                >
                    <Stack direction='row' spacing={1} alignItems='center'>
                        <Radio size='large' checked={isSelected} color='secondary' disabled={disabled} />
                        <Stack>
                            <Typography variant='h6' color={disabled ? 'primary.mui.inactive' : 'primary'}>
                                {title}
                            </Typography>
                            <Typography variant='body2' color='primary.mui.inactive'>
                                {subTitle}
                            </Typography>
                        </Stack>
                    </Stack>
                    {rightItem}
                </Stack>
                {description && (
                    <Typography
                        variant='body2'
                        color={disabled ? 'primary.mui.inactive' : 'primary'}
                        p={2}
                        textAlign='left'
                        width='100%'
                    >
                        {description}
                    </Typography>
                )}
            </Stack>
        </Tooltip>
    );
}
